import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Award, Clock, TrendingUp, Users, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { careers, testimonials, whyMIT } from '../mockData';

export const Home = () => {
  const featuredCareers = careers.filter((c) => ['administracion', 'contabilidad', 'mercadotecnia'].includes(c.id));

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-red-50"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-block px-4 py-2 bg-mit-blue/10 rounded-full mb-6">
                <span className="!font-semibold !text-sm text-mit-blue">Lo hacemos posible</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Obtén tu <span className="text-mit-blue">Título Técnico Profesional</span> a Nombre de la Nación
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Estudia una carrera técnica o valida tu experiencia laboral y titúlate en menos tiempo
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/carreras">
                  <Button size="lg" className="bg-mit-blue hover:bg-blue-800 text-white w-full sm:w-auto">
                    Ver Carreras
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/convalidacion">
                  <Button size="lg" variant="outline" className="border-2 border-mit-red text-mit-red hover:bg-mit-red hover:text-white w-full sm:w-auto">Convalidar mi Experiencia

                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_mit-formacion/artifacts/i1wsv4yt_DSC_0032.JPG.jpeg"
                  alt="Estudiantes MIT"
                  className="w-full h-[500px] object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              
              {/* Floating stats */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 animate-scale-in animate-delay-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-mit-red/10 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-mit-red" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10+</div>
                    <div className="text-sm text-gray-600">Carreras Técnicas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dos Rutas Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Elige tu Camino</h2>
            <p className="text-xl text-gray-600">Dos rutas para obtener tu título profesional</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ruta 1: Estudiar */}
            <Card className="hover-lift border-2 hover:border-mit-blue transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-mit-blue" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Soy Egresado / Quiero Estudiar</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Inicia tu formación técnica profesional desde cero. Programas de 3 años con enfoque práctico y docentes experimentados.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Formación completa de 3 años</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Prácticas en empresas del sector</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Modalidad presencial y semi-presencial</span>
                  </li>
                </ul>
                <Link to="/carreras">
                  <Button className="w-full bg-mit-blue hover:bg-blue-800 text-white">
                    Ver Carreras Disponibles
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Ruta 2: Convalidr */}
            <Card className="hover-lift border-2 hover:border-mit-red transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-mit-red/10 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="w-8 h-8 text-mit-red" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Tengo Experiencia Laboral</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Valida tu experiencia laboral y obtén tu título técnico en menor tiempo. Reconocemos tu trayectoria profesional.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Proceso acelerado de titulación</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Validación de experiencia laboral</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Título oficial a nombre de la nación</span>
                  </li>
                </ul>
                <Link to="/convalidacion">
                  <Button className="w-full bg-mit-red hover:bg-red-700 text-white">
                    Evaluar mi Experiencia
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Carreras Destacadas */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Carreras Destacadas</h2>
            <p className="text-xl text-gray-600">Programas técnicos de alta demanda laboral</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {featuredCareers.map((career, index) =>
            <Card key={career.id} className={`hover-lift overflow-hidden animate-fade-in-up animate-delay-${index * 100}`}>
                <div className="relative h-64 overflow-hidden">
                  <img
                  src={career.image}
                  alt={career.name}
                  className="w-full h-full object-cover hover-scale" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-mit-red text-white text-sm font-semibold rounded-full">
                      {career.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{career.name}</h3>
                  <p className="text-gray-600 mb-4">{career.shortDescription}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{career.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      <span>{career.modality}</span>
                    </div>
                  </div>
                  <Link to={`/carreras/${career.id}`}>
                    <Button variant="outline" className="w-full border-mit-blue text-mit-blue hover:bg-mit-blue hover:text-white">
                      Ver Detalles
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="text-center">
            <Link to="/carreras">
              <Button size="lg" variant="outline" className="border-2 border-gray-300 text-gray-700 hover:border-mit-blue hover:text-mit-blue">
                Ver Todas las Carreras
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why MIT */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">¿Por qué estudiar en MIT?</h2>
            <p className="text-xl text-gray-600">Formación técnica de calidad con respaldo institucional</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyMIT.map((item, index) => {
              const IconComponent = require('lucide-react')[item.icon];
              return (
                <Card key={index} className={`hover-lift animate-fade-in-up animate-delay-${index * 100}`}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-mit-blue" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Historias de Éxito</h2>
            <p className="text-xl text-gray-600">Nuestros egresados comparten su experiencia</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial, index) =>
            <Card key={testimonial.id} className={`hover-lift animate-fade-in-up animate-delay-${index * 100}`}>
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-mit-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCircle className="w-10 h-10 text-mit-blue" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-mit-blue">{testimonial.career}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-mit-blue text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Comienza tu Camino Profesional Hoy</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Obtén tu título técnico profesional y transforma tu futuro. Contáctanos por WhatsApp y recibe asesoría personalizada.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
              target="_blank"
              rel="noopener noreferrer">

              <Button size="lg" className="bg-white text-mit-blue hover:bg-gray-100 w-full sm:w-auto">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Contáctanos por WhatsApp
              </Button>
            </a>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-mit-blue w-full sm:w-auto">
                Más Información
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};